import React, { useState } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../firebase/config";
import EditableField from "./EditableField";
import { SPECIALTY_OPTIONS, STATE_OPTIONS, ENROLLMENT_OPTIONS } from "./constants";
import { formatDate, getFieldLabel, getStatusColor, getStatusText } from "./helpers";

// Application details modal with click-to-edit fields, edit history logging,
// restore-to-original, and fee schedule approval.
const ApplicationDetailsModal = ({
    submission,
    onClose,
    onOpenActivity,
    currentAdminEmail,
    showToast,
    onSubmissionChange,
    canApproveFeeSchedule,
    approvingFeeSchedule,
    onFeeScheduleApproval,
}) => {
    // Inline edit state
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [originalValue, setOriginalValue] = useState("");
    const [savingField, setSavingField] = useState(null);
    const [isRestoring, setIsRestoring] = useState(false);

    const startFieldEdit = (fieldName, currentValue) => {
        setEditingField(fieldName);
        setEditValue(currentValue || "");
        setOriginalValue(currentValue || "");
    };

    const cancelFieldEdit = () => {
        setEditingField(null);
        setEditValue("");
        setOriginalValue("");
    };

    const hasValueChanged = () => editValue !== originalValue;

    // Save single field
    const saveField = async (fieldName) => {
        if (editValue === originalValue) {
            cancelFieldEdit();
            return;
        }

        setSavingField(fieldName);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);

            // Create activity log entry for this edit
            const activityEntry = {
                id: Date.now().toString(),
                type: "edit",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                field: fieldName,
                fieldLabel: getFieldLabel(fieldName),
                oldValue: originalValue || "(empty)",
                newValue: editValue || "(empty)",
            };

            // If this is the first edit, save original data for restore functionality
            const updateData = {
                [fieldName]: editValue,
                lastModified: new Date(),
                activityLog: arrayUnion(activityEntry),
            };

            // Store original value if not already stored
            if (!submission.originalData) {
                updateData.originalData = {
                    name: submission.name || "",
                    email: submission.email || "",
                    phone: submission.phone || "",
                    specialty: submission.specialty || "",
                    practiceName: submission.practiceName || "",
                    dbaName: submission.dbaName || "",
                    location: submission.location || "",
                    address: submission.address || "",
                    city: submission.city || "",
                    zip: submission.zip || "",
                    crossStreets: submission.crossStreets || "",
                    practicePhone: submission.practicePhone || "",
                    practiceFax: submission.practiceFax || "",
                    availableHours: submission.availableHours || "",
                    urgentAppointments: submission.urgentAppointments || "",
                    npi: submission.npi || "",
                    taxId: submission.taxId || "",
                    licenseNumber: submission.licenseNumber || "",
                    referralEmail: submission.referralEmail || "",
                    billingContact: submission.billingContact || "",
                    billingEmail: submission.billingEmail || "",
                    message: submission.message || "",
                };
            }

            await updateDoc(docRef, updateData);
            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    [fieldName]: editValue,
                    lastModified: new Date(),
                    originalData: prev.originalData || updateData.originalData,
                    activityLog: [...(prev.activityLog || []), activityEntry],
                };
            });
            showToast(`${getFieldLabel(fieldName)} updated`);
            cancelFieldEdit();
        } catch (error) {
            console.error("Error saving field:", error);
            showToast("Failed to save. Please try again.", "error");
        } finally {
            setSavingField(null);
        }
    };

    // Restore to original state
    const handleRestoreOriginal = async () => {
        if (!submission.originalData) {
            showToast("No original data to restore", "error");
            return;
        }

        const confirmed = window.confirm(
            "Are you sure you want to restore this application to its original state?\n\nAll edits will be undone."
        );

        if (!confirmed) return;

        setIsRestoring(true);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            const restoreData = {
                ...submission.originalData,
                lastModified: null,
                originalData: null,
                restoredAt: new Date(),
            };

            await updateDoc(docRef, restoreData);
            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    ...submission.originalData,
                    lastModified: null,
                    originalData: null,
                    restoredAt: new Date(),
                };
            });
            showToast("Application restored to original state");
        } catch (error) {
            console.error("Error restoring:", error);
            showToast("Failed to restore. Please try again.", "error");
        } finally {
            setIsRestoring(false);
        }
    };

    // Render editable field via the shared component
    const renderEditableField = (fieldName, label, type = "text", options = null) => (
        <EditableField
            label={label}
            type={type}
            options={options}
            currentValue={submission[fieldName] || ""}
            isEditing={editingField === fieldName}
            editValue={editValue}
            onEditValueChange={setEditValue}
            onStartEdit={() => startFieldEdit(fieldName, submission[fieldName] || "")}
            onSave={() => saveField(fieldName)}
            onCancel={cancelFieldEdit}
            isSaving={savingField === fieldName}
            hasChanged={hasValueChanged()}
        />
    );

    return (
        <div
            className="modal-overlay"
            onClick={onClose}>
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Application Details</h2>
                    <div className="modal-header-actions">
                        <button
                            className="view-activity-btn-header"
                            onClick={() => onOpenActivity(submission)}
                        >
                            📋 Activity Log
                            {submission.activityLog?.length > 0 && (
                                <span className="activity-badge">{submission.activityLog.length}</span>
                            )}
                        </button>
                        <button
                            className="close-btn"
                            onClick={onClose}>
                            ×
                        </button>
                    </div>
                </div>
                <div className="modal-body">
                    {submission.lastModified && submission.originalData && (
                        <div className="edited-banner">
                            <div className="edited-banner-content">
                                <span className="edited-banner-icon">✏️</span>
                                <span className="edited-banner-text">
                                    This application was edited on {formatDate(submission.lastModified)}
                                </span>
                            </div>
                            <button
                                className="restore-btn"
                                onClick={handleRestoreOriginal}
                                disabled={isRestoring}
                            >
                                {isRestoring ? "Restoring..." : "↩️ Restore Original"}
                            </button>
                        </div>
                    )}
                    <p className="edit-hint-text">💡 Click any field to edit</p>
                    <div className="detail-grid">
                        <div className="detail-section">
                            <h3>Personal Information</h3>
                            {renderEditableField("name", "Name")}
                            {renderEditableField("email", "Email", "email")}
                            {renderEditableField("phone", "Phone", "tel")}
                            {renderEditableField("specialty", "Specialty", "select", SPECIALTY_OPTIONS)}
                        </div>

                        <div className="detail-section">
                            <h3>Practice Information</h3>
                            {renderEditableField("practiceName", "Practice Name")}
                            {renderEditableField("dbaName", "DBA Name")}
                            {renderEditableField("location", "State", "select", STATE_OPTIONS)}
                            {renderEditableField("address", "Address")}
                            {renderEditableField("city", "City")}
                            {renderEditableField("zip", "ZIP")}
                            {renderEditableField("practicePhone", "Practice Phone", "tel")}
                            {renderEditableField("practiceFax", "Practice Fax", "tel")}
                        </div>

                        <div className="detail-section">
                            <h3>Professional Details</h3>
                            {renderEditableField("npi", "NPI Number")}
                            {renderEditableField("taxId", "Tax ID")}
                            {renderEditableField("licenseNumber", "License Number")}
                            {renderEditableField("crossStreets", "Cross Streets")}
                            {renderEditableField("availableHours", "Available Hours")}
                            {renderEditableField("urgentAppointments", "Urgent Appointments", "select", [
                                { value: "yes", label: "Yes" },
                                { value: "no", label: "No" },
                            ])}
                        </div>

                        <div className="detail-section">
                            <h3>Additional Information</h3>
                            {renderEditableField("referralEmail", "Referral Email", "email")}
                            {renderEditableField("billingContact", "Billing Contact")}
                            {renderEditableField("billingEmail", "Billing Email", "email")}
                            <div className="detail-field-message">
                                <strong>Practice Description:</strong>
                                {editingField === "message" ? (
                                    <div className="edit-input-wrapper">
                                        <textarea
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            rows="4"
                                            autoFocus
                                        />
                                        <div className="edit-actions">
                                            {hasValueChanged() && (
                                                <button
                                                    className="save-field-btn"
                                                    onClick={() => saveField("message")}
                                                    disabled={savingField === "message"}
                                                    title="Save"
                                                >
                                                    {savingField === "message" ? "..." : "✓"}
                                                </button>
                                            )}
                                            <button
                                                className="cancel-field-btn"
                                                onClick={cancelFieldEdit}
                                                disabled={savingField === "message"}
                                                title="Cancel"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="message-content clickable"
                                        onClick={() => startFieldEdit("message", submission.message)}
                                        title="Click to edit"
                                    >
                                        {submission.message || "N/A"}
                                        <span className="edit-hint">✏️</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="detail-section">
                            <h3>Application Status</h3>
                            {renderEditableField("enrollmentType", "Enrollment Type", "select", ENROLLMENT_OPTIONS)}
                            <p>
                                <strong>Terms Accepted:</strong> {submission.termsAccepted ? "Yes" : "No"}
                            </p>
                            <p>
                                <strong>Digital Signature:</strong> {submission.signature ? "Yes" : "No"}
                            </p>
                            {submission.signature && (
                                <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                                    <strong>Signature:</strong>
                                    <div style={{
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        padding: "10px",
                                        background: "#fff",
                                        marginTop: "5px",
                                        display: "inline-block"
                                    }}>
                                        <img
                                            src={submission.signature}
                                            alt="Digital Signature"
                                            style={{ maxWidth: "300px", height: "auto" }}
                                        />
                                    </div>
                                </div>
                            )}
                            {submission.signedPdfUrl && (
                                <p>
                                    <strong>Signed Agreement:</strong>{" "}
                                    <a
                                        href={submission.signedPdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#00bfff" }}
                                    >
                                        📄 Download Signed PDF
                                    </a>
                                </p>
                            )}
                            <p>
                                <strong>Status:</strong>
                                <span className={`status-badge status-${getStatusColor(submission)}`}>{getStatusText(submission)}</span>
                            </p>

                            {/* Fee Schedule Approval Status */}
                            <div className="fee-schedule-status">
                                <strong>Fee Schedule:</strong>{" "}
                                {submission.feeScheduleApproved ? (
                                    <span className="fee-approved">
                                        ✓ Approved by {submission.feeScheduleApprovedByName || 'Admin'} on {formatDate(submission.feeScheduleApprovedAt)}
                                    </span>
                                ) : (
                                    <span className="fee-pending">Pending approval</span>
                                )}
                                {canApproveFeeSchedule && (
                                    <button
                                        className={`fee-toggle-btn ${submission.feeScheduleApproved ? 'remove' : 'approve'}`}
                                        onClick={() => onFeeScheduleApproval(submission, !submission.feeScheduleApproved)}
                                        disabled={approvingFeeSchedule === submission.id}
                                    >
                                        {approvingFeeSchedule === submission.id
                                            ? '...'
                                            : submission.feeScheduleApproved
                                                ? 'Remove Approval'
                                                : 'Approve Fee Schedule'}
                                    </button>
                                )}
                            </div>

                            {submission.lastModified && (
                                <p style={{ fontSize: "0.85rem", color: "#888", marginTop: "10px" }}>
                                    <em>Last modified: {formatDate(submission.lastModified)}</em>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetailsModal;
